import { AdventureStageAttempt } from '@/types/adventures';

interface StageProgressProps {
    currentStage: number;
    totalStages: number;
    stageAttempts?: AdventureStageAttempt[];
}

/**
 * StageProgress component displays the progress through a multi-stage adventure.
 * Shows completed, current, and upcoming stages with visual indicators.
 */
export function StageProgress({
    currentStage,
    totalStages,
    stageAttempts = [],
}: StageProgressProps) {
    // Create array of stage numbers
    const stages = Array.from({ length: totalStages }, (_, i) => i + 1);

    // Get the status of a stage
    const getStageStatus = (stageNumber: number): 'completed' | 'current' | 'upcoming' | 'failed' => {
        const attempt = stageAttempts.find(a => a.stage_number === stageNumber);
        if (attempt) {
            if (attempt.status.value === 'success' || attempt.status.value === 'partial') {
                return 'completed';
            }
            if (attempt.status.value === 'failure') {
                return 'failed';
            }
            if (attempt.status.value === 'in_progress' || attempt.status.value === 'pending') {
                return 'current';
            }
        }
        if (stageNumber < currentStage) return 'completed';
        if (stageNumber === currentStage) return 'current';
        return 'upcoming';
    };

    // Get color classes for a stage
    const getStageClasses = (status: 'completed' | 'current' | 'upcoming' | 'failed') => {
        switch (status) {
            case 'completed':
                return 'bg-green-600 text-white border-green-600';
            case 'current':
                return 'bg-purple-600 text-white border-purple-600 ring-2 ring-purple-400 ring-offset-2 ring-offset-slate-800';
            case 'failed':
                return 'bg-red-600 text-white border-red-600';
            case 'upcoming':
            default:
                return 'bg-slate-700 text-gray-400 border-slate-600';
        }
    };

    // Get connector line color
    const getConnectorColor = (fromStage: number) => {
        const status = getStageStatus(fromStage);
        if (status === 'completed') return 'bg-green-600';
        if (status === 'failed') return 'bg-red-600';
        return 'bg-slate-600';
    };

    return (
        <div className="flex items-center justify-center gap-1">
            {stages.map((stageNumber, index) => {
                const status = getStageStatus(stageNumber);
                const isLast = index === stages.length - 1;

                return (
                    <div key={stageNumber} className="flex items-center">
                        {/* Stage circle */}
                        <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all ${getStageClasses(status)}`}
                            title={`Stage ${stageNumber}: ${status}`}
                        >
                            {status === 'completed' ? (
                                <span className="text-xs">✓</span>
                            ) : status === 'failed' ? (
                                <span className="text-xs">✕</span>
                            ) : (
                                stageNumber
                            )}
                        </div>

                        {/* Connector line */}
                        {!isLast && (
                            <div className={`w-6 h-1 ${getConnectorColor(stageNumber)}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

interface StageProgressBarProps {
    currentStage: number;
    totalStages: number;
}

/**
 * Simple progress bar showing stage completion percentage.
 */
export function StageProgressBar({ currentStage, totalStages }: StageProgressBarProps) {
    const percentage = totalStages > 0 ? ((currentStage - 1) / totalStages) * 100 : 0;

    return (
        <div className="w-full">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Stage {currentStage} of {totalStages}</span>
                <span>{Math.round(percentage)}% complete</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                <div
                    className="h-2 bg-purple-600 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
}

export default StageProgress;
